// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Decodes a base64 presence bit mask into its bytes. The layout is described in
 * ai/context/backend/presence-bit-mask.md.
 */
export function toPresenceBits(presenceBitMask: string) {
  const decoded = atob(presenceBitMask);
  const presenceBits = new Uint8Array(decoded.length);

  // The Uint8Array.from callback form runs per character and measures far slower.
  for (let index = 0; index < decoded.length; index++) {
    presenceBits[index] = decoded.charCodeAt(index);
  }

  return presenceBits;
}

/**
 * Returns whether the bits mark the day at the given index present. Days outside the
 * window the server reported are absent, and the bound is dayCount rather than the byte
 * length because the final byte carries padding bits that are not days.
 */
export function hasPresenceOnDay(
  presenceBits: Uint8Array,
  dayIndex: number,
  dayCount: number
) {
  if (dayIndex < 0 || dayIndex >= dayCount) {
    return false;
  }

  const byteIndex = dayIndex >> 3;

  if (byteIndex >= presenceBits.length) {
    return false;
  }

  return ((presenceBits[byteIndex] >> (7 - (dayIndex & 7))) & 1) === 1;
}

/**
 * Returns whether an untyped grid row is marked present on the day at the given index.
 *
 * Rows reach the grid's accessors and its aggregation through different paths, and a group
 * row carries no mask at all, so the narrowing lives here rather than at each call site.
 */
export function isRowPresentOnDay(
  row: { presenceBits?: unknown },
  dayIndex: number,
  dayCount: number
) {
  const presenceBits: unknown = row.presenceBits;

  if (!(presenceBits instanceof Uint8Array)) {
    return false;
  }

  return hasPresenceOnDay(presenceBits, dayIndex, dayCount);
}
